import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Pagination, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AddBox, LibraryAdd, DeleteForever } from '@mui/icons-material';
import { BulkTextInputDialog } from 'components/dialogs/BulkTextInputDialog';

export interface MultiValueTextInputProps {
  values: (string | number)[];
  setValues: (values: (string | number)[]) => void;
  placeholder?: string;

  pageSize?: number;

  enforceDecimal?: boolean;
  enforceInteger?: boolean;
}

export function MultiValueTextInput(props: MultiValueTextInputProps): JSX.Element {
  const { i18n } = useTranslation();
  const { values, setValues, placeholder } = props;

  const totalValues = values.length;
  const rowsPerPage = props.pageSize || 10;
  const isPaginated = totalValues > rowsPerPage;
  const [currentValuesPage, setCurrentValuesPage] = useState<number>(1);

  const inputRefs = React.useRef<HTMLInputElement[]>([]);

  const [bulkValueInputIsOpen, setBulkValueInputIsOpen] = useState<boolean>(false);

  const focusAndSelectInput = (targetIndex: number) => {
    const targetInput = targetIndex >= 0 ? inputRefs.current[targetIndex] : inputRefs.current[inputRefs.current.length - 1];
    targetInput?.focus();
    targetInput?.select();
  }

  const delayedFocusAndSelectInput = (targetIndex: number) => {
    setTimeout(() => {
      focusAndSelectInput(targetIndex);
    }, 100);
  }

  const goToPage = (currentValuesPage: number, targetIndex?: number) => {
    setCurrentValuesPage(currentValuesPage);
    inputRefs.current = [];
    delayedFocusAndSelectInput(targetIndex || 0);
  }

  useEffect(() => {
    // If we have no values, add an empty one
    if (values.length === 0) {
      setValues([""]);
    }
  }, [values]);

  const fieldValuesPage = useMemo(() => {
    let displayedValues = values;
    if (isPaginated) {
      displayedValues = values.slice((currentValuesPage - 1) * rowsPerPage, currentValuesPage * rowsPerPage);
    }

    inputRefs.current = new Array(displayedValues.length).fill(null);

    return displayedValues.map((value, localIndex) => {
      const overallIndex = localIndex + ((currentValuesPage - 1) * rowsPerPage);

      return (
        // Container box, should have a right column for buttons and an adjustable left column for the value
        <Box key={localIndex} sx={{ display: "grid", gridTemplateColumns: "1fr auto", gridTemplateRows: "auto", mb: 1}}>
          <Box sx={{ gridRow: "1", gridColumn: "1", mr: 1 }}>
            <TextField
              autoComplete="off"
              autoFocus={localIndex === 0}
              inputRef={(ref) => inputRefs.current[localIndex] = ref}
              value={value}
              placeholder={placeholder}
              fullWidth
              onChange={(event) => {
                const newValues = [...values];
                let newValue: string | number = event.target.value;
                if (props.enforceDecimal) {
                  newValue = parseFloat(newValue);
                  newValue = isNaN(newValue) ? "" : newValue;
                } else if (props.enforceInteger) {
                  newValue = parseInt(newValue);
                  newValue = isNaN(newValue) ? "" : newValue;
                }
                newValues[overallIndex] = newValue;
                setValues(newValues);
              }}
              onKeyDown={(event) => {
                if (event.key === "Tab") {
                  event.stopPropagation();
                  event.preventDefault();
                  const isLastValue = overallIndex === values.length - 1;
                    // if shift is held, focus on the previous input if there is one
                  if (event.shiftKey) {
                    if (localIndex > 0) {
                      focusAndSelectInput(localIndex - 1);
                    } else if (localIndex == 0) {
                      // if we're at the first input and not on the first page, go to the last input on the previous page
                      if (currentValuesPage > 1) {
                        goToPage(currentValuesPage - 1, -1);
                      }
                    }
                  } else {
                    if (!isLastValue) {
                      if (localIndex < rowsPerPage - 1) {
                        focusAndSelectInput(localIndex + 1);
                      } else {
                        // if we're at the last input and not on the last page, go to the first input on the next page
                        if (currentValuesPage < Math.ceil(values.length / rowsPerPage)) {
                          goToPage(currentValuesPage + 1);
                        }
                      }
                    } else if (isLastValue) {
                      // if we're at the last value, add a new value and focus on it
                      const newValues = [...values];
                      newValues.push("");
                      setValues(newValues);
                      // If it's also the last item on the page, go to the next page
                      if (localIndex == rowsPerPage - 1) {
                        goToPage(currentValuesPage + 1);
                      } else {
                        delayedFocusAndSelectInput(localIndex + 1);
                      }
                  }
                  }
                }
              }}
            />
          </Box>
          <Box sx={{ gridRow: "1", gridColumn: "2", alignContent: "center" }}>
            <IconButton
              onClick={() => {
                const newValues = [...values];
                newValues.splice(overallIndex, 1);
                setValues(newValues);
              }}>
              <DeleteForever />
            </IconButton>
          </Box>
        </Box>
      );
    });
  }, [values, currentValuesPage]);

  return (
    <>
      {fieldValuesPage}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1, gap: 1}}>
          <Button
            startIcon={<LibraryAdd />}
            onClick={() => setBulkValueInputIsOpen(true)}>
            {i18n.t("query-form.bulk-add-values")}
          </Button>
          {bulkValueInputIsOpen &&
            <BulkTextInputDialog
              initialValues={values}
              onClose={() => setBulkValueInputIsOpen(false)}
              onSubmit={(newValues: string[]) => {
                setValues([...newValues]);
                setBulkValueInputIsOpen(false);
              }}
            />
          }
          <Button
            variant="contained"
            startIcon={<AddBox />}
            onClick={() => {
              // Add a new value to the list, then focus on it
              const newValues = [...values];
              newValues.push("");
              setValues(newValues);
              goToPage(Math.ceil(newValues.length / rowsPerPage), -1);
            }}>
            {i18n.t("query-form.add-value")}
          </Button>
        </Box>
        {totalValues > rowsPerPage &&
          <Pagination
            count={Math.ceil(totalValues / rowsPerPage)}
            sx={{ display: "flex", justifyContent: "center", mt: 1 }}
            page={currentValuesPage}
            onChange={(_event: React.ChangeEvent<unknown>, page: number) => goToPage(page)}
            shape="rounded"
            size="small"
            showFirstButton
            showLastButton />
        }
    </>
  );
}
